function bufToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let s = ''
  bytes.forEach((b) => { s += String.fromCharCode(b) })
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function b64urlToBuf(s: string): ArrayBuffer {
  const pad = s.replace(/-/g, '+').replace(/_/g, '/')
  const padded = pad + '='.repeat((4 - (pad.length % 4)) % 4)
  const bin = atob(padded)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out.buffer
}

function reviveCreation(options: Record<string, unknown>): PublicKeyCredentialCreationOptions {
  const user = options.user as Record<string, unknown>
  const exclude = options.excludeCredentials as Array<Record<string, unknown>> | undefined
  return {
    ...options,
    challenge: b64urlToBuf(String(options.challenge)),
    user: {
      ...user,
      id: b64urlToBuf(String(user.id)),
    },
    excludeCredentials: exclude?.map((c) => ({
      ...c,
      id: b64urlToBuf(String(c.id)),
    })),
  } as PublicKeyCredentialCreationOptions
}

function reviveRequest(options: Record<string, unknown>): PublicKeyCredentialRequestOptions {
  const allow = options.allowCredentials as Array<Record<string, unknown>> | undefined
  return {
    ...options,
    challenge: b64urlToBuf(String(options.challenge)),
    allowCredentials: allow?.map((c) => ({
      ...c,
      id: b64urlToBuf(String(c.id)),
    })),
  } as PublicKeyCredentialRequestOptions
}

function credentialToJson(cred: PublicKeyCredential): Record<string, unknown> {
  const response = cred.response as AuthenticatorAttestationResponse & AuthenticatorAssertionResponse
  const json: Record<string, unknown> = {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64url(response.clientDataJSON),
    },
  }
  const resp = json.response as Record<string, unknown>
  if ('attestationObject' in response && response.attestationObject) {
    resp.attestationObject = bufToB64url(response.attestationObject)
  }
  if ('authenticatorData' in response && response.authenticatorData) {
    resp.authenticatorData = bufToB64url(response.authenticatorData)
  }
  if ('signature' in response && response.signature) {
    resp.signature = bufToB64url(response.signature)
  }
  if ('userHandle' in response && response.userHandle) {
    resp.userHandle = bufToB64url(response.userHandle)
  }
  const att = response as AuthenticatorAttestationResponse
  if (typeof att.getTransports === 'function') {
    resp.transports = att.getTransports()
  }
  return json
}

export async function createPasskey(options: Record<string, unknown>): Promise<Record<string, unknown>> {
  const cred = await navigator.credentials.create({ publicKey: reviveCreation(options) })
  if (!cred || !(cred instanceof PublicKeyCredential)) {
    throw new DOMException('cancelled', 'NotAllowedError')
  }
  return credentialToJson(cred)
}

export async function getPasskey(options: Record<string, unknown>): Promise<Record<string, unknown>> {
  const cred = await navigator.credentials.get({ publicKey: reviveRequest(options) })
  if (!cred || !(cred instanceof PublicKeyCredential)) {
    throw new DOMException('cancelled', 'NotAllowedError')
  }
  return credentialToJson(cred)
}

export function isWebAuthnCancel(error: unknown): boolean {
  return error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'AbortError')
}
